import type { DeviationSettingValue } from 'api/digitalTwin/digitalTwin.types'
import i18n from 'i18next'
import Datetime from 'utils/datetime/datetime'

//Period string in deviation settings list tree view
export function getSettingValue(
  activeValue?: string | DeviationSettingValue | number | null,
  defaultValue?: string | DeviationSettingValue | number | null,
  baseValue?: string | DeviationSettingValue | number | null,
  isTimeSeries?: boolean,
  attribute?: string
): string {
  if (activeValue === null || activeValue === undefined) {
    return '-'
  }

  let output = `${activeValue}`

  if (isTimeSeries) {
    // Temporary fix until we are able to show a better value for time series settings.
    output = i18n.t('Time series')
  } else if (attribute && attribute.includes(`forced`) && activeValue === 1) {
    output = i18n.t(`Active`)
  } else if (attribute && attribute.includes(`forced`) && activeValue === 0) {
    output = i18n.t(`Forced driving inactive`)
  } else if (attribute && attribute.includes(`availability`) && activeValue === 1) {
    output = i18n.t(`Unavailability inactive`)
  } else if (attribute && attribute.includes(`availability`) && activeValue === 0) {
    output = i18n.t(`Active`)
  }

  // For settings that are not 'forced' or 'availability', add the default value in parenthesis at the end
  if (!attribute?.includes('forced') && !attribute?.includes(`availability`) && !isTimeSeries) {
    const defaultSuffix = baseValue ?? defaultValue
    if (defaultSuffix !== null && defaultSuffix !== undefined) {
      output = `${output} (${defaultSuffix})`
    }
  }
  return output
}

//Period string in deviation settings list tree view
export function getPeriodString(settingStartTime: ISODateTime | null, settingEndTime: ISODateTime | null): string {
  if (settingEndTime && settingStartTime) {
    return `${Datetime.toLocalTime(settingStartTime, 'longDayAndMonthTextWithoutYearIfCurrentYear')} - ${Datetime.toLocalTime(settingEndTime, 'longDayAndMonthTextWithoutYearIfCurrentYear')}`
  }

  if (settingStartTime && !settingEndTime) {
    return `${Datetime.toLocalTime(settingStartTime, 'longDayAndMonthTextWithoutYearIfCurrentYear')} - ${i18n.t('Until further notice')}`
  }

  return ''
}

export function getUnit(measurementUnit: string | null): string {
  return measurementUnit ? ` ` + measurementUnit : ``
}

// Generic function to split settings into previous, active, and upcoming
export function splitSettingsByTime<T extends { start_time: string | null | undefined; end_time?: string | null }>(
  settings: T[],
  referenceTime: { startTime: ISODateTime; endTime?: ISODateTime }
) {
  const previous: T[] = []
  const active: T[] = []
  const upcoming: T[] = []

  settings.forEach((setting) => {
    if (!setting.start_time || !referenceTime) {
      return
    }

    const start = Datetime.toISOString(setting.start_time)
    const end = setting.end_time ? Datetime.toISOString(setting.end_time) : null
    const twelveHoursAgo = Datetime.subtractHours(referenceTime.startTime, 12)
    const twelveHoursAhead = referenceTime.endTime && Datetime.addHours(referenceTime.endTime, 12)

    if (
      referenceTime.endTime &&
      Datetime.isBeforeOrEqual(start, referenceTime.endTime) &&
      (!end || Datetime.isAfterOrEqual(end, referenceTime.startTime))
    ) {
      active.push(setting)
    } else if (end && Datetime.isBefore(end, referenceTime.startTime) && Datetime.isAfterOrEqual(end, twelveHoursAgo)) {
      previous.push(setting)
    } else if (
      referenceTime.endTime &&
      Datetime.isAfter(start, referenceTime.endTime) &&
      twelveHoursAhead &&
      Datetime.isBeforeOrEqual(start, twelveHoursAhead)
    ) {
      upcoming.push(setting)
    }
  })
  return { previous, active, upcoming }
}
